import { Vector3 } from 'three';
import { mergeMeshes, SweepMesh } from '../geometry/sweep';
import { PRINT_ARROW_PROFILE, trackSpineDrop } from '../geometry/profile';
import { Frame } from '../track/frames';
import { printMmToWorldMetres } from './types';

/** Physical click-fit clearance in print millimetres. */
export const JOINT_CLEARANCE_MM = 0.25;

function clearanceM(): number {
  return printMmToWorldMetres(JOINT_CLEARANCE_MM);
}

/** Pin radius in world metres (~2.8 mm at 1:100) — support joints. */
export const JOINT_PIN_RADIUS_M = 0.28;
/** Smaller track pin (~1.6 mm at 1:100); female outer size stays the same. */
export const TRACK_PIN_RADIUS_M = 0.16;
/** Pin stick-out length in world metres (~5.5 mm at 1:100). */
export const JOINT_PIN_LENGTH_M = 0.55;
/** Sleeve outer wall thickness in world metres (solid-looking collar). */
export const JOINT_SLEEVE_WALL_M = 0.18;
/** Key flat inset on the D-profile (world metres). */
export const JOINT_KEY_FLAT_M = 0.09;
/** Track key flat scaled with the smaller pin. */
export const TRACK_KEY_FLAT_M = 0.05;
/** Extra sleeve / cavity depth past the pin length (open tube, no back cap). */
export const JOINT_SOCKET_PLUG_M = 0.12;
/** How far the cavity cutter sticks past the mouth so the end face stays open. */
const CAVITY_MOUTH_OVERSHOOT_M = 0.08;
/** Track sleeve outside radius — kept at the old (larger-pin) outer size. */
export const TRACK_SLEEVE_OUTER_RADIUS_M =
  JOINT_PIN_RADIUS_M + printMmToWorldMetres(JOINT_CLEARANCE_MM) + JOINT_SLEEVE_WALL_M;

const SEG = 14;

export interface JointBasis {
  origin: Vector3;
  /** Unit axis from the part into the mate (pin points this way). */
  dir: Vector3;
  right: Vector3;
  up: Vector3;
}

export function jointBasis(
  origin: Vector3,
  dirIn: Vector3,
  rightIn?: Vector3,
  upIn?: Vector3,
): JointBasis {
  const dir = dirIn.clone().normalize();
  let right: Vector3;
  let up: Vector3;
  if (rightIn && upIn) {
    // Project track frame onto plane ⊥ tangent, then orthonormalise.
    right = rightIn.clone().addScaledVector(dir, -rightIn.dot(dir));
    if (right.lengthSq() < 1e-10) {
      right = upIn.clone().addScaledVector(dir, -upIn.dot(dir));
    }
    right.normalize();
    up = new Vector3().crossVectors(dir, right).normalize();
  } else {
    const tmp = Math.abs(dir.y) < 0.9 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
    right = new Vector3().crossVectors(dir, tmp).normalize();
    up = new Vector3().crossVectors(right, dir).normalize();
  }
  return { origin: origin.clone(), dir, right, up };
}

/** Centre of the track spine/base at a frame (below the rails). */
export function trackSpineJointOrigin(f: Frame): Vector3 {
  return f.railPos.clone().addScaledVector(f.up, -trackSpineDrop(PRINT_ARROW_PROFILE));
}

/** D-shaped radius at angle (flat on −right side for keying). */
function dRadius(angle: number, radius: number, flat: number): number {
  const c = Math.cos(angle);
  if (c < -0.15) {
    const maxX = radius - flat;
    const rFlat = maxX / Math.max(1e-6, -c);
    return Math.min(radius, rFlat);
  }
  return radius;
}

/**
 * Male keyed pin: D-profile solid sticking out from `origin` along `dir`.
 */
export function pinMesh(
  origin: Vector3,
  dir: Vector3,
  right?: Vector3,
  up?: Vector3,
  pinRadius = JOINT_PIN_RADIUS_M,
  keyFlat = JOINT_KEY_FLAT_M,
): SweepMesh {
  const basis = jointBasis(origin, dir, right, up);
  const tip = basis.origin.clone().addScaledVector(basis.dir, JOINT_PIN_LENGTH_M);
  return solidProfilePrism(basis.origin, tip, basis, pinRadius, keyFlat);
}

/**
 * Female keyed socket collar: open hollow tube (both ends open) so the pin can click in.
 * Pair with {@link sleeveCavityMesh} and boolean-difference after union so the hole survives.
 */
export function sleeveMesh(
  origin: Vector3,
  dir: Vector3,
  right?: Vector3,
  up?: Vector3,
  pinRadius = JOINT_PIN_RADIUS_M,
  keyFlat = JOINT_KEY_FLAT_M,
  outerRadius?: number,
): SweepMesh {
  const basis = jointBasis(origin, dir, right, up);
  const depth = JOINT_PIN_LENGTH_M + JOINT_SOCKET_PLUG_M;
  const back = basis.origin.clone().addScaledVector(basis.dir, -depth);

  const innerR = pinRadius + clearanceM();
  const outerR = outerRadius ?? innerR + JOINT_SLEEVE_WALL_M;
  const flat = keyFlat + clearanceM() * 0.5;

  return openTubeSocketMesh(basis.origin, back, basis, innerR, outerR, flat);
}

/**
 * Solid D-cutter for the female socket void. Subtract from the unioned part so the
 * click-fit hole stays open (union alone would fill it with spine/column material).
 */
export function sleeveCavityMesh(
  origin: Vector3,
  dir: Vector3,
  right?: Vector3,
  up?: Vector3,
  pinRadius = JOINT_PIN_RADIUS_M,
  keyFlat = JOINT_KEY_FLAT_M,
): SweepMesh {
  const basis = jointBasis(origin, dir, right, up);
  const innerR = pinRadius + clearanceM();
  const flat = keyFlat + clearanceM() * 0.5;
  const mouth = basis.origin.clone().addScaledVector(basis.dir, CAVITY_MOUTH_OVERSHOOT_M);
  const back = basis.origin
    .clone()
    .addScaledVector(basis.dir, -(JOINT_PIN_LENGTH_M + JOINT_SOCKET_PLUG_M * 0.5));
  return solidProfilePrism(mouth, back, basis, innerR, flat);
}

/** Track pin seated in the spine/base, keyed to the track frame (smaller than support pins). */
export function trackPinMesh(f: Frame): SweepMesh {
  return pinMesh(
    trackSpineJointOrigin(f),
    f.tan,
    f.right,
    f.up,
    TRACK_PIN_RADIUS_M,
    TRACK_KEY_FLAT_M,
  );
}

/**
 * Track sleeve collar in the spine/base — outer size unchanged, open for click-fit.
 * Male pin uses +tan (into the next chunk); sleeveMesh extends along −dir, so the
 * female axis is flipped to −tan (shared right/up) so the collar sits inside this chunk.
 */
export function trackSleeveMesh(f: Frame): SweepMesh {
  const origin = trackSpineJointOrigin(f);
  const basis = jointBasis(origin, f.tan, f.right, f.up);
  const femaleDir = basis.dir.clone().multiplyScalar(-1);
  return sleeveMesh(
    origin,
    femaleDir,
    basis.right,
    basis.up,
    TRACK_PIN_RADIUS_M,
    TRACK_KEY_FLAT_M,
    TRACK_SLEEVE_OUTER_RADIUS_M,
  );
}

/** Track sleeve cavity cutter (subtract after Manifold union). Same axis flip as {@link trackSleeveMesh}. */
export function trackSleeveCavityMesh(f: Frame): SweepMesh {
  const origin = trackSpineJointOrigin(f);
  const basis = jointBasis(origin, f.tan, f.right, f.up);
  const femaleDir = basis.dir.clone().multiplyScalar(-1);
  return sleeveCavityMesh(
    origin,
    femaleDir,
    basis.right,
    basis.up,
    TRACK_PIN_RADIUS_M,
    TRACK_KEY_FLAT_M,
  );
}

/* ------------------------------------------------------------------ *
 * Square (rectangular) track connector — Track Type 2 beam part.
 *
 * Type 1 keys the click-fit into the round spine below the rails
 * ({@link trackPinMesh}/{@link trackSleeveMesh}). Type 2 has no round spine;
 * its lowest member is a rectangular beam bar. These builders drop a
 * rectangular male/female into that beam so the connector matches the beam's
 * cross-section: the female outer envelope equals the beam width × height and
 * the male peg is inset by a wall so the two chunks click together.
 * ------------------------------------------------------------------ */

/** Rectangular peg size as a fraction of the beam half-extent (rest is socket wall). */
export const TYPE2_PEG_FRACTION = 0.6;
/** Target socket wall in world metres (~0.6 print mm), clamped to the beam so thin beams still key. */
export const TYPE2_MIN_WALL_M = printMmToWorldMetres(0.6);

/** Resolved half-extents for a Type 2 beam connector (per axis: across-track W, vertical H). */
export interface Type2JointExtents {
  /** Male peg half-extents. */
  pegHalfW: number;
  pegHalfH: number;
  /** Female cavity half-extents (peg + clearance). */
  cavityHalfW: number;
  cavityHalfH: number;
  /** Female outer half-extents — equal to the beam part (per the brief). */
  outerHalfW: number;
  outerHalfH: number;
}

/**
 * Size a rectangular click-fit to a beam of the given half-extents. The female
 * outer stays flush with the beam; the cavity is inset by a wall and the peg by
 * a further clearance, each axis clamped so very thin beams still produce a
 * printable (non-degenerate) socket.
 */
export function type2JointExtents(beamHalfW: number, beamHalfH: number): Type2JointExtents {
  const clr = clearanceM();
  const axis = (halfB: number): { pegHalf: number; cavityHalf: number } => {
    const wall = Math.min(TYPE2_MIN_WALL_M, halfB * 0.35);
    const cavityHalf = Math.max(halfB - wall, 1e-4);
    // Peg is the smaller of the target fraction and "cavity minus clearance",
    // but never collapses below half the cavity for a thin beam.
    const pegHalf = Math.max(Math.min(halfB * TYPE2_PEG_FRACTION, cavityHalf - clr), cavityHalf * 0.5);
    return { pegHalf, cavityHalf };
  };
  const w = axis(Math.max(beamHalfW, 1e-4));
  const h = axis(Math.max(beamHalfH, 1e-4));
  return {
    pegHalfW: w.pegHalf,
    pegHalfH: h.pegHalf,
    cavityHalfW: w.cavityHalf,
    cavityHalfH: h.cavityHalf,
    outerHalfW: Math.max(beamHalfW, 1e-4),
    outerHalfH: Math.max(beamHalfH, 1e-4),
  };
}

/** Male rectangular peg: solid box prism sticking out from `origin` along `dir`. */
export function boxPinMesh(
  origin: Vector3,
  dir: Vector3,
  right: Vector3,
  up: Vector3,
  halfW: number,
  halfH: number,
  length = JOINT_PIN_LENGTH_M,
): SweepMesh {
  const basis = jointBasis(origin, dir, right, up);
  const tip = basis.origin.clone().addScaledVector(basis.dir, length);
  return boxPrism(basis.origin, tip, basis, halfW, halfH);
}

/**
 * Female rectangular socket: open box tube (both ends open), outer flush with the
 * beam and inner sized to the cavity. Extends along −dir like {@link sleeveMesh}
 * (callers flip to −tan so the collar lives inside the receiving chunk). Pair with
 * {@link boxSleeveCavityMesh} and boolean-difference after union to keep the hole.
 */
export function boxSleeveMesh(
  origin: Vector3,
  dir: Vector3,
  right: Vector3,
  up: Vector3,
  innerHalfW: number,
  innerHalfH: number,
  outerHalfW: number,
  outerHalfH: number,
  length = JOINT_PIN_LENGTH_M + JOINT_SOCKET_PLUG_M,
): SweepMesh {
  const basis = jointBasis(origin, dir, right, up);
  const back = basis.origin.clone().addScaledVector(basis.dir, -length);
  return openBoxTube(basis.origin, back, basis, innerHalfW, innerHalfH, outerHalfW, outerHalfH);
}

/** Solid box cutter for the female cavity — subtract after union so the click-fit stays open. */
export function boxSleeveCavityMesh(
  origin: Vector3,
  dir: Vector3,
  right: Vector3,
  up: Vector3,
  innerHalfW: number,
  innerHalfH: number,
): SweepMesh {
  const basis = jointBasis(origin, dir, right, up);
  const mouth = basis.origin.clone().addScaledVector(basis.dir, CAVITY_MOUTH_OVERSHOOT_M);
  const back = basis.origin
    .clone()
    .addScaledVector(basis.dir, -(JOINT_PIN_LENGTH_M + JOINT_SOCKET_PLUG_M * 0.5));
  return boxPrism(mouth, back, basis, innerHalfW, innerHalfH);
}

/**
 * Solid branch junction ball — fills the crotch where tubes meet so the join is not hollow.
 * Radius should match the beam radius (sphere diameter = beam diameter).
 */
export function solidJunctionMesh(pos: Vector3, radius: number): SweepMesh {
  return sphereMeshSolid(pos, Math.max(radius, 1e-4));
}

/**
 * Pin radius that fits inside a beam of the given radius (for mid-beam connectors).
 * Scaled down from the default support pin when the tube is thinner.
 */
export function beamFitPinRadius(beamRadius: number): number {
  return Math.min(JOINT_PIN_RADIUS_M, Math.max(beamRadius * 0.65, TRACK_PIN_RADIUS_M));
}

/** Key flat scaled with {@link beamFitPinRadius}. */
export function beamFitKeyFlat(pinRadius: number): number {
  return pinRadius * (JOINT_KEY_FLAT_M / JOINT_PIN_RADIUS_M);
}

function solidProfilePrism(
  a: Vector3,
  b: Vector3,
  basis: JointBasis,
  radius: number,
  flat: number,
): SweepMesh {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  const rings = [a, b];

  for (const center of rings) {
    for (let i = 0; i < SEG; i++) {
      const ang = (i / SEG) * Math.PI * 2;
      const r = flat > 1e-9 ? dRadius(ang, radius, flat) : radius;
      const nx = Math.cos(ang);
      const ny = Math.sin(ang);
      positions.push(
        center.x + basis.right.x * nx * r + basis.up.x * ny * r,
        center.y + basis.right.y * nx * r + basis.up.y * ny * r,
        center.z + basis.right.z * nx * r + basis.up.z * ny * r,
      );
      const n = basis.right
        .clone()
        .multiplyScalar(nx)
        .addScaledVector(basis.up, ny)
        .normalize();
      normals.push(n.x, n.y, n.z);
    }
  }

  for (let i = 0; i < SEG; i++) {
    const i1 = (i + 1) % SEG;
    indices.push(i, SEG + i, i1, i1, SEG + i, SEG + i1);
  }

  const bot = SEG * 2;
  const top = bot + 1;
  positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
  const back = basis.dir.clone().multiplyScalar(-1);
  normals.push(back.x, back.y, back.z, basis.dir.x, basis.dir.y, basis.dir.z);
  for (let i = 0; i < SEG; i++) {
    const i1 = (i + 1) % SEG;
    // Cap winding must oppose the side-wall ring edges (orientable solid).
    indices.push(bot, i, i1);
    indices.push(top, SEG + i1, SEG + i);
  }

  return {
    positions: Float32Array.from(positions),
    normals: Float32Array.from(normals),
    indices: Uint32Array.from(indices),
  };
}

/**
 * Open keyed tube (both ends open): outer wall, inner wall, mouth + back annuli.
 * No end cap — pin clicks in; use {@link sleeveCavityMesh} + difference after union.
 */
function openTubeSocketMesh(
  mouth: Vector3,
  back: Vector3,
  basis: JointBasis,
  innerR: number,
  outerR: number,
  flat: number,
): SweepMesh {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  const pushRing = (center: Vector3, radius: number, useFlat: boolean) => {
    const base = positions.length / 3;
    for (let i = 0; i < SEG; i++) {
      const ang = (i / SEG) * Math.PI * 2;
      const r = useFlat ? dRadius(ang, radius, flat) : radius;
      const nx = Math.cos(ang);
      const ny = Math.sin(ang);
      positions.push(
        center.x + basis.right.x * nx * r + basis.up.x * ny * r,
        center.y + basis.right.y * nx * r + basis.up.y * ny * r,
        center.z + basis.right.z * nx * r + basis.up.z * ny * r,
      );
      normals.push(0, 0, 0);
    }
    return base;
  };

  const oMouth = pushRing(mouth, outerR, false);
  const oBack = pushRing(back, outerR, false);
  const iMouth = pushRing(mouth, innerR, true);
  const iBack = pushRing(back, innerR, true);

  const quad = (a0: number, a1: number, b0: number, b1: number, flip = false) => {
    if (flip) indices.push(a0, a1, b0, a1, b1, b0);
    else indices.push(a0, b0, a1, a1, b0, b1);
  };

  for (let i = 0; i < SEG; i++) {
    const i1 = (i + 1) % SEG;
    quad(oMouth + i, oMouth + i1, oBack + i, oBack + i1);
    quad(iMouth + i, iMouth + i1, iBack + i, iBack + i1, true);
    // Mouth annulus (open)
    indices.push(oMouth + i, oMouth + i1, iMouth + i);
    indices.push(oMouth + i1, iMouth + i1, iMouth + i);
    // Back annulus (open — no cap)
    indices.push(oBack + i, iBack + i, oBack + i1);
    indices.push(oBack + i1, iBack + i, iBack + i1);
  }

  return {
    positions: Float32Array.from(positions),
    normals: Float32Array.from(normals),
    indices: Uint32Array.from(indices),
  };
}

/** Rectangle corner offsets (right, up), CCW. */
function boxCorners(halfW: number, halfH: number): [number, number][] {
  return [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ];
}

/**
 * Solid rectangular prism between ring centres `a` and `b`, cross-section
 * `right·±halfW + up·±halfH`, capped both ends. Winding matches
 * {@link solidProfilePrism} so the orientable-solid rules stay identical.
 */
function boxPrism(
  a: Vector3,
  b: Vector3,
  basis: JointBasis,
  halfW: number,
  halfH: number,
): SweepMesh {
  const corners = boxCorners(halfW, halfH);
  const N = corners.length;
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  for (const center of [a, b]) {
    for (const [u, v] of corners) {
      positions.push(
        center.x + basis.right.x * u + basis.up.x * v,
        center.y + basis.right.y * u + basis.up.y * v,
        center.z + basis.right.z * u + basis.up.z * v,
      );
      const n = basis.right.clone().multiplyScalar(u).addScaledVector(basis.up, v).normalize();
      normals.push(n.x, n.y, n.z);
    }
  }

  for (let i = 0; i < N; i++) {
    const i1 = (i + 1) % N;
    indices.push(i, N + i, i1, i1, N + i, N + i1);
  }

  const bot = N * 2;
  const top = bot + 1;
  positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
  const back = basis.dir.clone().multiplyScalar(-1);
  normals.push(back.x, back.y, back.z, basis.dir.x, basis.dir.y, basis.dir.z);
  for (let i = 0; i < N; i++) {
    const i1 = (i + 1) % N;
    indices.push(bot, i, i1);
    indices.push(top, N + i1, N + i);
  }

  return {
    positions: Float32Array.from(positions),
    normals: Float32Array.from(normals),
    indices: Uint32Array.from(indices),
  };
}

/**
 * Open rectangular tube (both ends open): outer wall, inner wall, mouth + back
 * annuli, no end cap. Rectangular twin of {@link openTubeSocketMesh}; normals are
 * left zero because these feed the Manifold boolean pipeline, which recomputes them.
 */
function openBoxTube(
  mouth: Vector3,
  back: Vector3,
  basis: JointBasis,
  innerHalfW: number,
  innerHalfH: number,
  outerHalfW: number,
  outerHalfH: number,
): SweepMesh {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  const N = 4;

  const pushRing = (center: Vector3, halfW: number, halfH: number) => {
    const base = positions.length / 3;
    for (const [u, v] of boxCorners(halfW, halfH)) {
      positions.push(
        center.x + basis.right.x * u + basis.up.x * v,
        center.y + basis.right.y * u + basis.up.y * v,
        center.z + basis.right.z * u + basis.up.z * v,
      );
      normals.push(0, 0, 0);
    }
    return base;
  };

  const oMouth = pushRing(mouth, outerHalfW, outerHalfH);
  const oBack = pushRing(back, outerHalfW, outerHalfH);
  const iMouth = pushRing(mouth, innerHalfW, innerHalfH);
  const iBack = pushRing(back, innerHalfW, innerHalfH);

  const quad = (a0: number, a1: number, b0: number, b1: number, flip = false) => {
    if (flip) indices.push(a0, a1, b0, a1, b1, b0);
    else indices.push(a0, b0, a1, a1, b0, b1);
  };

  for (let i = 0; i < N; i++) {
    const i1 = (i + 1) % N;
    quad(oMouth + i, oMouth + i1, oBack + i, oBack + i1);
    quad(iMouth + i, iMouth + i1, iBack + i, iBack + i1, true);
    // Mouth annulus (open)
    indices.push(oMouth + i, oMouth + i1, iMouth + i);
    indices.push(oMouth + i1, iMouth + i1, iMouth + i);
    // Back annulus (open — no cap)
    indices.push(oBack + i, iBack + i, oBack + i1);
    indices.push(oBack + i1, iBack + i, iBack + i1);
  }

  return {
    positions: Float32Array.from(positions),
    normals: Float32Array.from(normals),
    indices: Uint32Array.from(indices),
  };
}

function sphereMeshSolid(center: Vector3, radius: number, segments = 12): SweepMesh {
  const stacks = Math.max(6, Math.floor(segments / 2));
  const slices = segments;
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i <= stacks; i++) {
    const v = i / stacks;
    const phi = v * Math.PI;
    const y = Math.cos(phi);
    const r = Math.sin(phi);
    for (let j = 0; j <= slices; j++) {
      const u = j / slices;
      const theta = u * Math.PI * 2;
      const x = r * Math.cos(theta);
      const z = r * Math.sin(theta);
      positions.push(center.x + x * radius, center.y + y * radius, center.z + z * radius);
      normals.push(x, y, z);
    }
  }

  for (let i = 0; i < stacks; i++) {
    for (let j = 0; j < slices; j++) {
      const a = i * (slices + 1) + j;
      const b = a + slices + 1;
      // CCW when viewed from outside → positive volume for Manifold.
      indices.push(a, a + 1, b, a + 1, b + 1, b);
    }
  }

  return {
    positions: Float32Array.from(positions),
    normals: Float32Array.from(normals),
    indices: Uint32Array.from(indices),
  };
}

export function mergeWithJoints(base: SweepMesh, joints: SweepMesh[]): SweepMesh {
  if (joints.length === 0) return base;
  return mergeMeshes([base, ...joints]);
}
